import { useEffect, useRef, useState } from 'react'
import { useAuthContext } from '../../context/auth.context'
import { useApiFormLaunch } from '../../api-new/state/auth'

// Whether this environment can open the web viewer at all: auth's
// GET /api/v1/launch/available answers { available }, false where no viewer
// is configured (VIEWER_PUBLIC_ORIGIN unset) -- there POST /api/v1/launch
// answers 503 launch.viewer_not_configured. The course's "Launch" and
// the staff sidebar's "Web viewer" stay hidden until it says true: no
// control that can only fail.
//
// Read once per session (the token's session, else the login) and shared by
// every caller: the sidebar's two copies (desktop column and phone drawer),
// every course's deep-link dialog and every page change reuse one answer. A
// remount after the answer came back shows it at once. A failed read counts
// as unavailable and is forgotten, so a later mount asks again.
//
// `enabled` is the caller's own gate (signed in): nobody else is ever shown
// the controls, so nobody else asks.

const cache = { key: null, promise: null, value: undefined }

// Tests start each case from a fresh session.
export const forgetViewerAvailable = () => {
  cache.key = null
  cache.promise = null
  cache.value = undefined
}

const readOnce = (key, fetch) => {
  if (cache.key !== key || !cache.promise) {
    cache.key = key
    cache.value = undefined
    const promise = Promise.resolve()
      .then(fetch)
      .then(answer => {
        const available = !!answer && answer.available === true
        if (cache.promise === promise) cache.value = available
        return available
      })
      .catch(() => {
        if (cache.promise === promise) cache.promise = null
        return false
      })
    cache.promise = promise
  }
  return cache.promise
}

const sessionKey = state => {
  if (!state) return null
  const claims = state.jwtClaims || {}
  const key = claims.sessionID || state.loginID
  return key ? String(key) : null
}

export const useViewerAvailable = enabled => {
  const { state } = useAuthContext()
  const key = enabled ? sessionKey(state) : null

  // api-new hooks have a new identity every render: read through a ref so
  // the effect runs once per session, not once per render.
  const apiFormLaunch = useApiFormLaunch()
  const apiRef = useRef(apiFormLaunch)
  apiRef.current = apiFormLaunch

  const [answer, setAnswer] = useState(null)

  useEffect(() => {
    if (!key) return
    let live = true
    readOnce(key, () => apiRef.current.get('available')).then(value => {
      if (live) setAnswer({ key, value })
    })
    return () => {
      live = false
    }
  }, [key])

  if (!key) return false
  if (answer && answer.key === key) return answer.value
  return cache.key === key && cache.value === true
}

// Who is offered a course's "Launch", wherever it appears (the Deep link
// dialog, the course page's header, the course card's ⋮): one rule, so they
// can't disagree. Since the web viewer opened to every signed-in user (owner
// 2026-10-08) that is anyone signed in who can see the course, where this
// environment has a viewer: a launch elsewhere can only fail. Whether the
// course has a web build is the caller's other half (use-course-web-builds).
export const useCanOpenInBrowser = () => {
  const { state } = useAuthContext()
  const signedIn = !!(state && state.isAuth !== false && state.loginID)
  return useViewerAvailable(signedIn) && signedIn
}
